import React from 'react';
/* global window */
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import hoistNonReactStatic from 'hoist-non-react-statics';
import Auth from '../../Auth';
import { getChatStatus } from 'ragnarok-web/dist/Components/features/ChatStatusHelper/ChatStatusHelper';
import ChatModule from '../../asyncChat';
import cookieManagerHelper from 'Helpers/cookies';
import { setChatStatus, setUnreadCount } from 'Actions/chatManager';
import toggleUnreadChats from 'Actions/toggleUnreadChats';
import { withConfig } from 'HOCs/withConfig/withConfig';
import { ContextMessageProvider } from 'Components/MessageProvider/MessageContext';
import withTrack from 'HOCs/withTrack/withTrack';
import { getSelectedLang } from 'Selectors/getLanguages';
import withRouter from 'HOCs/withRouter';
import { get } from 'Server';
import { urls as urlsItems } from 'Reducers/items';
import { urls as urlsUsers } from 'Reducers/users';
import { XMPPEventEmitter } from 'ragnarok-web/dist/XMPPEventEmitter/XMPPEventEmitter';
import { getUserInfoByCategory } from 'Helpers/users';

const withChat = WrappedComponent => {
    class WithChat extends React.Component {
        static propTypes = {
            location: PropTypes.object
        };

        constructor(props) {
            super(props);
            this.chatConnAvailable = false;
            this.chatConfig = props.marketConfig.get('chat');
            this.events = [];
            this.state = {
                mamState: null
            };
            this.theme = props.marketConfig.get('theme', 'id');
        }

        componentDidMount() {
            this.createChatConnection();
            this.props.setChatStatus(getChatStatus());
        }

        componentDidUpdate(prevProps) {
            if (!this.chatConnAvailable) {
                this.createChatConnection();
            }
            if (prevProps.location.pathname.includes('/nf/chat') && (!this.props.location.pathname.includes('/nf/chat'))) {
                this.attachXMPPEvents();
            }
        }

        componentWillUnmount() {
            this.detachXMPPEvents();
        }

        attachXMPPEvents = () => {
            XMPPEventEmitter.unsubscribeAll();

            this.events = [
                XMPPEventEmitter.subscribe('onReceive', this.getUnreadThreads),
                XMPPEventEmitter.subscribe('onInterventionChange', this.onInterventionChange),
                XMPPEventEmitter.subscribe('onInterventionChange', this.onInterventionChange),
                XMPPEventEmitter.subscribe('onMamComplete', mamState => {
                    this.setState({ mamState });
                    this.getUnreadThreads();
                }),
                XMPPEventEmitter.subscribe('refreshItem', this.refreshItem),
                XMPPEventEmitter.subscribe('refreshUser', this.refreshUser),
                XMPPEventEmitter.subscribe('onStreamError', this.onStreamError)
            ];
        }
        detachXMPPEvents = () => this.events.forEach(unsub => unsub());

        checkSystemMsg = (msg, userId, itemId, type, typeParams, callback, extras = {}, lead_info = {}) => {
            if (this.xmpp && this.xmpp.connEstablished) {
                this.xmpp.getLastMessageByThread(userId, itemId, () => {
                    const item = this.props.itemsCollection[itemId] || this.props.myZoneAdsCollection[itemId];
                    const user = this.props.usersCollection[userId];
                    const categoryId = extras?.categoryId || item?.category_id;

                    const dealer = getUserInfoByCategory(user, categoryId);
                    const dealerType = (dealer.dealer_type || '').toLowerCase();
                    const isTestDriveEnabled = user?.enabled_features?.findIndex(f => f === 'store_test_drive' || f === 'home_test_drive') > -1;
                    const sellerId = item?.user_id;
                    const messageParams = { message: msg, userId, itemId, categoryId, dealerType, isTestDriveEnabled, type, typeParams, extras, lead_info, sellerId };
                    const msgId = this.xmpp.sendMessage(messageParams);

                    this.props.track('item_chat_tap_send', {
                        resultset_id: msgId
                    });
                    if (typeof callback === 'function') {
                        return callback();
                    }

                    return undefined;
                });
            }
        };

        refreshItem = itemId => {
            this.props.getItem(itemId);
        }

        refreshUser = userId => {
            const ids = userId.toString();

            this.props.getUsers({ ids });
        }

        onStreamError = (ErrorState = {}) => {
            const { condition } = ErrorState;

            if (condition === 'policy-violation') {
                if (this.xmpp) {
                    this.xmpp.logout();
                }
                Auth.logout(() => {
                    this.props.track('login_sign_out');
                    window.location.replace('/');
                });
            }
        }

        sendChatMessage = (msg, userId, itemId, type, typeParams, callback, extras = {}, lead_info = {}) => {
            const { variant } = this.props;

            this.chatMessage = msg;
            if (this.xmpp && this.xmpp.connEstablished) {
                this.chatMessage = null;
                this.checkSystemMsg(msg, userId, itemId, type, typeParams, callback, extras, lead_info);
            }
            else if (Auth.isLoggedIn()) {
                const config = this.getChatConfig();

                this.events.push(XMPPEventEmitter.subscribe('onMamComplete', this.checkMamState(msg, userId, itemId, type, typeParams, callback, extras, lead_info)));

                ChatModule.importChatWrapper().then(({ 'default': ChatWrapper }) => {
                    ChatWrapper.instantiateXMPP((module, interventionData, systemMessageData) => {
                        const params = {
                            config: this.props.marketConfig.get('chat').newChat,
                            interventionData,
                            systemMessageData,
                            variant
                        };

                        module.getInstance(params).then(instance => {
                            this.xmpp = instance;
                            this.xmpp.sendPresence();
                            this.xmpp.setTracker(this.props.track);
                            if (this.xmpp && this.xmpp.connEstablished) {
                                if (this.chatMessage) {
                                    this.chatMessage = null;
                                    this.checkSystemMsg(msg, userId, itemId, type, typeParams, callback, extras, lead_info);
                                }
                            }
                        });
                        window.removeEventListener('beforeunload', this.onClose, false);
                        window.addEventListener('beforeunload', this.onClose, false);
                    }, config, this.theme);
                });
            }
        };

        checkMamState = (msg, userId, itemId, type, typeParams, callback, extras = {}, lead_info = {}) => ({ state }) => {
            if (this.xmpp && this.xmpp.connEstablished && state === 'end' && this.chatMessage) {
                this.chatMessage = null;
                this.checkSystemMsg(msg, userId, itemId, type, typeParams, callback, extras, lead_info);
            }
        };

        getChatConfig = () => {
            const chatConfig = this.chatConfig && this.chatConfig.newChat;
            const localeObj = { lang: this.props.getSelectedLang };

            return { ...chatConfig, ...localeObj };
        }

        createChatConnection = () => {
            const config = this.getChatConfig();
            const { variant } = this.props;

            if (typeof window !== 'undefined' && Auth.isLoggedIn() && window.localStorage) {
                if (window.localStorage.getItem('__ct__bifro_ct') || cookieManagerHelper.readCookie('ct')) {
                    ChatModule.importChatWrapper().then(({ 'default': ChatWrapper }) => {
                        ChatWrapper.loadOfflineThreads(Auth.getUser());
                        ChatWrapper.instantiateXMPP((module, interventionData, systemMessageData) => {
                            const params = {
                                config: this.props.marketConfig.get('chat').newChat,
                                interventionData,
                                systemMessageData,
                                variant
                            };

                            if (!module.instanceExists()) {
                                this.attachXMPPEvents();
                                module.getInstance(params).then(instance => {
                                    this.xmpp = instance;
                                    this.xmpp.sendPresence();
                                    this.xmpp.setTracker(this.props.track);
                                });
                                window.removeEventListener('beforeunload', this.onClose, false);
                                window.addEventListener('beforeunload', this.onClose, false);
                            }
                        }, config, this.theme);
                    });
                    this.chatConnAvailable = true;
                }
                else {
                    this.getToken();
                    this.chatConnAvailable = true;
                }
            }
        };

        onInterventionChange = objects => {
            if (objects && objects.length) {
                objects.forEach(obj => {
                    const data = obj.data;
                    const intervData = this.props.interventionData[data.intervention_id];

                    if (data && intervData) {
                        this.props.track(obj.event, Object.assign({}, data,
                            {
                                intervention_type: intervData.type,
                                shown_at: intervData.shownWindow.join('/'),
                                intellichat_label: intervData.text
                            }
                        ));
                    }
                });
            }
        };

        getToken = () => {
            Auth.regenerateToken(token => {
                if (token) {
                    this.createChatConnection();
                }
            });
        };

        onClose = () => {
            this.chatConnAvailable = true;
            return this.xmpp && this.xmpp.logout(false);
        };

        getUnreadThreads = () => {
            this.xmpp.getUnreadThreads(count => {
                this.setNotificationCount(count);
            });
        };

        setNotificationCount = count => {
            this.props.toggleUnreadChats(count > 0);
            this.props.setUnreadCount(count);
        };

        render() {
            const value = {
                sendChatMessage: this.sendChatMessage,
                mamState: this.state.mamState
            };

            return (
                <ContextMessageProvider value={ value }>
                    <WrappedComponent { ...this.props } data-aut-id="withChatComponent" />
                </ContextMessageProvider>
            );
        }
    }

    WithChat.propTypes = {
        itemsCollection: PropTypes.object,
        usersCollection: PropTypes.object,
        toggleUnreadChats: PropTypes.func,
        interventionData: PropTypes.object,
        setChatStatus: PropTypes.func,
        marketConfig: PropTypes.shape({
            get: PropTypes.func.isRequired
        }).isRequired,
        track: PropTypes.func,
        setUnreadCount: PropTypes.func,
        getSelectedLang: PropTypes.string,
        getItem: PropTypes.func,
        getUsers: PropTypes.func,
        variant: PropTypes.string,
        loginModalState: PropTypes.object,
        myZoneAdsCollection: PropTypes.object
    };

    const mapStateToProps = (state, ownProps) => ({
        itemsCollection: state.items?.elements,
        myZoneAdsCollection: state.myZone?.adsData,
        usersCollection: state.users?.elements,
        interventionData: state.interventionsData,
        getSelectedLang: getSelectedLang(state, ownProps),
        loginModalState: state.loginModalState
    });

    const mapDispatchToProps = dispatch => ({
        setChatStatus: state => dispatch(setChatStatus(state)),
        toggleUnreadChats: state => dispatch(toggleUnreadChats(state)),
        setUnreadCount: state => dispatch(setUnreadCount(state)),
        getItem: id => dispatch(get(urlsItems.getItem(id), 'ELEMENT_ITEMS')),
        getUsers: params => dispatch(get(urlsUsers.getBulkUsers, 'BULK_USERS', params))
    });

    const connectedComponent = compose(
        withConfig('marketConfig'),
        withRouter,
        connect(mapStateToProps, mapDispatchToProps),
        withTrack
    )(WithChat);

    connectedComponent.displayName = `withChat(${WrappedComponent.displayName || WrappedComponent.name})`;

    return hoistNonReactStatic(connectedComponent, WrappedComponent);
};

export default withChat;
